import { useState, useEffect } from 'react'
import { createMetricsClient } from '@strata-monorepo/metrics/lib/factory'
import { initializeNewRelic } from '../questionnaire/base/NewRelic/NewRelicSnippet'
import type { MetricsClient } from '@strata-monorepo/metrics/lib/types/MetricsClient'

interface MetricsState {
  metricsClient?: MetricsClient
  error: Error | null
}

export const useMetrics = (): MetricsState => {
  const [state, setState] = useState<MetricsState>({
    metricsClient: undefined,
    error: null,
  })

  useEffect(() => {
    let isMounted = true

    const initializeMetricsClient = async (): Promise<void> => {
      try {
        await initializeNewRelic()

        const metricsClient = createAppropriateMetricsClient()

        if (isMounted) {
          setState({
            metricsClient,
            error: null,
          })

          metricsClient.increment('fmp.initialized', 1)
        }
      } catch (error) {
        console.error('[Metrics] Failed to initialize')

        if (isMounted) {
          // Fallback to Console adapter
          const fallbackClient = createMetricsClient('Console')
          setState({
            metricsClient: fallbackClient,
            error: error as Error,
          })
        }
      }
    }

    initializeMetricsClient()

    return (): void => {
      isMounted = false
    }
  }, [])

  return state
}

export const createAppropriateMetricsClient = (): MetricsClient => {
  const isDevelopment = import.meta.env.VITE_ENVIRONMENT === 'development'

  if (window.newrelic && !isDevelopment) {
    console.log('[Metrics] Setting Up New Relic Browser Adapter')
    return createMetricsClient('NewRelicBrowser', window.newrelic)
  }

  console.log('[Metrics] Setting Up Console Adapter')
  return createMetricsClient('Console')
}
